import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import { licenseService, License } from '../../../services/license';
import { Pencil, Trash2 } from 'lucide-react';

const LicensesManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<License[]>([]);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      navigate('/dashboard', { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await licenseService.getAll();
      setList(res || []);
    } catch (error) {
      console.error('Error loading licenses:', error);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('آیا از حذف این مجوز اطمینان دارید؟')) return;
    try {
      await licenseService.delete(id);
      load();
    } catch (error) {
      console.error('Error deleting license:', error);
      alert('خطا در حذف مجوز');
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">مدیریت مجوزها</h1>
          <p className="text-gray-500 mt-1">مدیریت تصاویر و اطلاعات مجوزها و گواهینامه‌ها</p>
        </div>
        <button
          onClick={() => navigate('/admin/licenses/new')}
          className="px-6 py-2.5 rounded-xl bg-[#0e444e] text-white hover:bg-[#0b363e] shadow-lg shadow-[#0e444e]/20 font-medium transition-all"
        >
          افزودن مجوز جدید
        </button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">تصویر</th>
                <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">عنوان</th>
                <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">توضیحات</th>
                <th className="text-center py-4 px-6 text-sm font-semibold text-gray-600 w-32">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-gray-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-gray-500">
                    هیچ مجوزی یافت نشد
                  </td>
                </tr>
              ) : (
                list.map((license) => (
                  <tr key={license.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="w-24 h-16 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                        <img 
                          src={license.image_url} 
                          alt={license.title} 
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="font-medium text-gray-900">{license.title}</div><span className={`inline-block mt-2 px-2 py-1 rounded-full text-[10px] ${license.title.toUpperCase().includes('ISO') ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>{license.title.toUpperCase().includes('ISO') ? 'گواهینامه مدیریتی' : 'مجوز قانونی و صنفی'}</span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm text-gray-500 line-clamp-2">{license.description}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => navigate(`/admin/licenses/${license.id}`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="ویرایش"
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(license.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="حذف"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LicensesManagementPage;
